import React, { useState, useEffect } from "react"
import { parentDashboardApi } from "../../lib/api"
import ParentAssignmentSection from "./ParentAssignmentSection"

const AllStudentsPanel = ({ students, termId }) => {
  const [allAssignments, setAllAssignments] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchAllStudentDetails()
  }, [students, termId])

  const fetchAllStudentDetails = async () => {
    try {
      setLoading(true)
      setError(null)

      // Fetch details for all students in parallel
      const details = await Promise.all(
        students.map(student => parentDashboardApi.getStudentDetail(student.id))
      )

      // Filter by term if one is selected
      const filterByTerm = (assignments) => {
        if (!termId) return assignments
        return (assignments || []).filter(a => a.course?.term_id === termId)
      }

      // Combine all assignments by category
      const combined = {
        past_due: [],
        due_today: [],
        due_this_week: [],
        submitted_today: [],
        recently_submitted: [],
        graded_today: [],
        recently_graded: [],
        completed: [],
      }

      details.forEach((data, index) => {
        const student = students[index]
        const assignments = data.student?.assignments || {}

        // Add student info to each assignment for context, then filter by term
        const addStudentInfoAndFilter = (assignments) =>
          filterByTerm(assignments).map(a => ({ ...a, student_name: student.name, student_id: student.id }))

        combined.past_due.push(...addStudentInfoAndFilter(assignments.past_due))
        combined.due_today.push(...addStudentInfoAndFilter(assignments.due_today))
        combined.due_this_week.push(...addStudentInfoAndFilter(assignments.due_this_week))
        combined.submitted_today.push(...addStudentInfoAndFilter(assignments.submitted_today))
        combined.recently_submitted.push(...addStudentInfoAndFilter(assignments.recently_submitted))
        combined.graded_today.push(...addStudentInfoAndFilter(assignments.graded_today))
        combined.recently_graded.push(...addStudentInfoAndFilter(assignments.recently_graded))
        combined.completed.push(...addStudentInfoAndFilter(assignments.completed))
      })

      // Sort by due date (earliest first) for active assignments
      const sortByDueDate = (a, b) => {
        if (!a.due_at) return 1
        if (!b.due_at) return -1
        return new Date(a.due_at) - new Date(b.due_at)
      }

      combined.past_due.sort(sortByDueDate)
      combined.due_today.sort(sortByDueDate)
      combined.due_this_week.sort(sortByDueDate)

      setAllAssignments(combined)
    } catch (err) {
      setError(err.message || "Failed to load student details")
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-[var(--cream)] rounded-lg w-1/3"></div>
            <div className="h-24 bg-[var(--cream)] rounded-xl"></div>
            <div className="h-24 bg-[var(--cream)] rounded-xl"></div>
            <div className="h-24 bg-[var(--cream)] rounded-xl"></div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="p-8 text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-red-50 flex items-center justify-center">
            <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <p className="text-[var(--ink-muted)] mb-4">{error}</p>
          <button
            onClick={fetchAllStudentDetails}
            className="px-4 py-2 bg-[var(--ink)] text-white rounded-xl hover:bg-[var(--ink-muted)] transition-colors font-medium text-sm"
          >
            Try again
          </button>
        </div>
      </div>
    )
  }

  if (!allAssignments) {
    return null
  }

  const totalPastDue = allAssignments.past_due.length
  const totalToday = allAssignments.due_today.length
  const totalThisWeek = allAssignments.due_this_week.length

  return (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[var(--ink)] to-[var(--ink-muted)] opacity-95" />

        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <pattern id="grid-all" width="10" height="10" patternUnits="userSpaceOnUse">
                <circle cx="1" cy="1" r="1" fill="white" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid-all)" />
          </svg>
        </div>

        <div className="relative p-6 text-white">
          <div className="flex items-center gap-4">
            {/* Icon */}
            <div className="w-16 h-16 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg">
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>

            {/* Info */}
            <div>
              <h2 className="font-display text-2xl font-bold">Combined Overview</h2>
              <p className="text-white/80 text-sm">
                {students.length} {students.length === 1 ? "student" : "students"}
              </p>
            </div>
          </div>

          {/* Stats summary */}
          <div className="mt-5 flex flex-wrap gap-3 text-sm">
            <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg ${totalPastDue > 0 ? "bg-white/20" : "bg-white/10"}`}>
              <span className="w-2 h-2 rounded-full bg-red-400" />
              <strong>{totalPastDue}</strong> overdue
            </span>
            <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg ${totalToday > 0 ? "bg-white/20" : "bg-white/10"}`}>
              <span className="w-2 h-2 rounded-full bg-amber-400" />
              <strong>{totalToday}</strong> today
            </span>
            <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/10">
              <span className="w-2 h-2 rounded-full bg-blue-400" />
              <strong>{totalThisWeek}</strong> this week
            </span>
          </div>
        </div>
      </div>

      {/* Assignment sections */}
      <div className="p-4 sm:p-6 space-y-2">
        <ParentAssignmentSection
          title="Past Due"
          assignments={allAssignments.past_due}
          urgency="overdue"
          emptyMessage="No overdue assignments"
          showStudentName
          collapsed
        />

        <ParentAssignmentSection
          title="Due Today"
          assignments={allAssignments.due_today}
          urgency="today"
          emptyMessage="Nothing due today"
          showStudentName
          collapsed
        />

        <ParentAssignmentSection
          title="Due This Week"
          assignments={allAssignments.due_this_week}
          urgency="this_week"
          emptyMessage="Nothing else due this week"
          showStudentName
          collapsed
        />

        <ParentAssignmentSection
          title="Submitted Today"
          assignments={allAssignments.submitted_today}
          urgency="submitted_today"
          showStudentName
          collapsed
        />

        <ParentAssignmentSection
          title="Recently Submitted"
          assignments={allAssignments.recently_submitted}
          urgency="recently_submitted"
          showStudentName
          collapsed
        />

        <ParentAssignmentSection
          title="Graded Today"
          assignments={allAssignments.graded_today}
          urgency="graded_today"
          showStudentName
          collapsed
        />

        <ParentAssignmentSection
          title="Recently Graded"
          assignments={allAssignments.recently_graded}
          urgency="graded"
          showStudentName
          collapsed
        />

        <ParentAssignmentSection
          title="Completed"
          assignments={allAssignments.completed}
          urgency="completed"
          showStudentName
          collapsed
        />
      </div>
    </div>
  )
}

export default AllStudentsPanel
